import { MouseEvent, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  DevHubAlert,
  DevHubBox,
  DevHubButton,
  DevHubCircularProgress,
  DevHubDynamicIcon,
  DevHubGrid,
  DevHubListItemSecondaryAction,
  DevHubMenu,
  DevHubMenuItem,
  DevHubTooltip,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { Progress } from '@backstage/core-components';
import { Device } from '../types/InsightsTypes';
import DeviceSearch from './DeviceSearch';
import DeviceCard from './DeviceCard';
import { CSVLink } from 'react-csv';
import { asExportableDevicesForAudit, sortDevices } from '../../../util/export';
import LaunchIcon from '@material-ui/icons/Launch';
import { GetDevicesQuery } from '../queries/InsightsQueries';

const ADD_DEVICE_URL =
  'https://comcast.service-now.com/iop?id=sc_category&sys_id=83af56cedba5dc5853fb5a58dc961961';
const DECOM_PHYSICAL_URL = 'https://workflow.cable.comcast.com/hds';

/**
 * Entry point into App360 device overview tab
 */
function ApplicationDevices() {
  const { entity } = useEntity();
  const [devices, setDevices] = useState<Device[]>([] as Device[]);
  const [hasMoreResults, setHasMoreResults] = useState<boolean>(true);
  const [searchResults, setSearchResults] = useState<Device[]>([] as Device[]);
  const [selectedEnvs, setSelectedEnvs] = useState([] as string[]);
  const [selectedTiers, setSelectedTiers] = useState([] as string[]);
  const [devOptsAnchorEl, setDevOptsAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [dataForExport, setDataForExport] = useState<Device[]>([] as Device[]);
  const [isLoading, setIsLoading] = useState(false);
  const [readyToDownload, setReadyToDownload] = useState(false);
  const [errorOccurred, setErrorOccurred] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [deviceOffset, setDeviceOffset] = useState(0);
  const [totalDeviceCount, setTotalDeviceCount] = useState(0);

  useEffect(() => {
    if (readyToDownload) {
      document.getElementById('hidden-csv-link')?.click();
    }
    setReadyToDownload(false);
  }, [readyToDownload]);

  // Grab ITRC Id from application annotation yaml entry
  const appId: number = Number(
    entity?.metadata?.annotations?.['comcast.net/itrc-id'],
  );

  const { loading, error, data, fetchMore } = useQuery(GetDevicesQuery, {
    variables: {
      applicationId: appId,
      pagination: {
        limit: 100,
      },
    },
  });

  const handleEnvChange = (env: string) => {
    const isSelected = selectedEnvs?.includes(env);

    if (isSelected) {
      setSelectedEnvs(selectedEnvs?.filter(e => e !== env));
    } else {
      setSelectedEnvs([...selectedEnvs, env]);
    }
  };

  const handleTierChange = (tier: string) => {
    const isSelected = selectedTiers?.includes(tier);

    if (isSelected) {
      setSelectedTiers(selectedTiers?.filter(t => t !== tier));
    } else {
      setSelectedTiers([...selectedTiers, tier]);
    }
  };

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          pagination: {
            limit: 100,
            offset: devices?.length ?? 0,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          const fetchedDevices =
            fetchMoreResult?.resources?.application?.devicesPaged?.devices ??
            [];

          if (fetchedDevices.length === 0) {
            setHasMoreResults(false);
            return prev;
          }

          return {
            ...prev,
            resources: {
              ...prev.resources,
              application: {
                ...prev.resources.application,
                devicesPaged: {
                  ...prev.resources.application.devicesPaged,
                  totalCount:
                    fetchMoreResult.resources?.application?.devicesPaged
                      ?.totalCount,
                  devices: [
                    ...(prev.resources.application.devicesPaged.devices ?? []),
                    ...fetchedDevices,
                  ],
                },
              },
            },
          };
        },
      });
    } finally {
      setLoadingMore(false);
    }
  };

  const filteredDevices = useMemo((): Device[] => {
    let result = searchResults?.length > 0 ? [...searchResults] : [...devices];

    if (selectedEnvs.length > 0) {
      result = result.filter((device: Device) =>
        selectedEnvs.includes(device.environment),
      );
    }

    if (selectedTiers.length > 0) {
      result = result.filter((device: Device) =>
        selectedTiers.includes(device.tier),
      );
    }

    return result.sort((deviceA: Device, deviceB: Device) => {
      const missingValuesA =
        deviceA.tier === null ||
        deviceA.tier === undefined ||
        deviceA.environment === null ||
        deviceA.environment === undefined;
      const missingValuesB =
        deviceB.tier === null ||
        deviceB.tier === undefined ||
        deviceB.environment === null ||
        deviceB.environment === undefined;

      if (missingValuesA && !missingValuesB) {
        return -1;
      }

      if (!missingValuesA && missingValuesB) {
        return 1;
      }

      return 0;
    });
  }, [devices, searchResults, selectedEnvs, selectedTiers]);

  const exportData = filteredDevices;

  const getLoadMoreButtonLabel = (): string => {
    if (loadingMore) {
      return 'Loading...';
    }

    if (hasMoreResults && devices.length > 0) {
      return 'Load More';
    }

    return 'No More Devices';
  };

  useEffect(() => {
    if (data) {
      setDevices(data?.resources?.application?.devicesPaged?.devices);

      const currentTotal =
        data?.resources?.application?.devicesPaged?.totalCount ?? 0;

      if (currentTotal > 0) {
        setHasMoreResults(
          (data?.resources?.application?.devicesPaged?.devices?.length ?? 0) <
            currentTotal,
        );
      }
    }
  }, [data]);

  const handleDevOptsClick = (event: MouseEvent<HTMLButtonElement>) => {
    setDevOptsAnchorEl(event.currentTarget);
  };

  const handleDevOptsClose = (): void => {
    setDevOptsAnchorEl(null);
  };

  const handleDownload = async () => {
    const totalCount = data?.resources?.application?.devicesPaged?.totalCount;
    setTotalDeviceCount(totalCount);
    if (!totalCount) return;
    if (totalCount === dataForExport.length) {
      setReadyToDownload(true);
      return;
    }

    setIsLoading(true);
    let allDevices: Device[] = [];

    for (let i = 0; i < totalCount; i += 100) {
      try {
        setDeviceOffset(i);
        const result = await fetchMore({
          variables: { pagination: { limit: 100, offset: i } },
          updateQuery: (prev, { fetchMoreResult }) => fetchMoreResult || prev,
        });

        const fetchedDevices =
          result.data?.resources?.application?.devicesPaged?.devices || [];

        allDevices = [...allDevices, ...fetchedDevices];
      } catch (err) {
        setErrorOccurred(true);
      }
    }

    setDataForExport(allDevices);
    setIsLoading(false);
    setReadyToDownload(true);
  };

  const ExportButton = () => {
    return errorOccurred ? (
      <DevHubTooltip title="Error occurred while exporting">
        <span>
          <DevHubDynamicIcon
            style={{ marginLeft: '0.5rem' }}
            iconName="ReportProblem"
            color="error"
            fontSize="large"
          />
        </span>
      </DevHubTooltip>
    ) : (
      <>
        {isLoading ? (
          <DevHubCircularProgress />
        ) : (
          <DevHubButton
            variant="contained"
            color="primary"
            onClick={handleDownload}
          >
            Export
          </DevHubButton>
        )}
      </>
    );
  };

  return (
    <>
      {loading ? (
        <Progress />
      ) : (
        <>
          <DevHubGrid container alignItems="center" spacing={2}>
            <DevHubGrid item xs={6}>
              <DevHubBox display="flex" alignItems="center">
                <DevHubTypography variant="h2">Find Devices</DevHubTypography>
                <DevHubTooltip
                  placement="top"
                  title="For a device to be rendered in the below please make sure it has an Environment and Tier assigned in IOP."
                  type="icon"
                  icon="help"
                />
              </DevHubBox>
            </DevHubGrid>
            <DevHubGrid
              container
              item
              xs={6}
              alignItems="center"
              justifyContent="flex-end"
            >
              <DevHubGrid item>
                <DevHubButton
                  aria-controls="simple-menu"
                  aria-haspopup="true"
                  endIcon={<DevHubDynamicIcon iconName="ExpandMore" />}
                  onClick={handleDevOptsClick}
                >
                  Device Options
                </DevHubButton>
                <DevHubMenu
                  id="simple-menu"
                  anchorEl={devOptsAnchorEl}
                  keepMounted
                  open={Boolean(devOptsAnchorEl)}
                  onClose={handleDevOptsClose}
                >
                  <DevHubMenuItem
                    component="a"
                    href={ADD_DEVICE_URL}
                    onClick={handleDevOptsClose}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Add Device
                    <DevHubListItemSecondaryAction>
                      <LaunchIcon fontSize="small" />
                    </DevHubListItemSecondaryAction>
                  </DevHubMenuItem>
                  <DevHubMenuItem
                    component="a"
                    href={DECOM_PHYSICAL_URL}
                    onClick={handleDevOptsClose}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Decommission Device
                    <DevHubListItemSecondaryAction>
                      <LaunchIcon fontSize="small" />
                    </DevHubListItemSecondaryAction>
                  </DevHubMenuItem>
                </DevHubMenu>
              </DevHubGrid>
            </DevHubGrid>
          </DevHubGrid>
          <DeviceSearch
            setSearchResults={setSearchResults}
            searchResults={searchResults}
            appId={appId}
            handleEnvChange={handleEnvChange}
            selectedEnvs={selectedEnvs}
            handleTierChange={handleTierChange}
            selectedTiers={selectedTiers}
            deviceList={devices}
          />

          {!error && (
            <DevHubBox my={2} display="flex" justifyContent="space-between">
              <DevHubTypography variant="body2">
                Showing {filteredDevices.length} of{' '}
                {data?.resources?.application?.devicesPaged?.totalCount ?? 0}{' '}
                devices
              </DevHubTypography>
              {(selectedEnvs.length > 0 || selectedTiers.length > 0) && (
                <DevHubTypography variant="body2">
                  Active filters: {selectedEnvs.length + selectedTiers.length}
                </DevHubTypography>
              )}
            </DevHubBox>
          )}

          {exportData?.length > 0 && (
            <DevHubGrid item xs={12}>
              <DevHubBox display="flex" justifyContent="space-between" m={0}>
                <DevHubGrid item xs={11}>
                  <DevHubBox mr={1} mb={1.5}>
                    {isLoading && !errorOccurred && (
                      <DevHubAlert variant="filled" severity="info">
                        Exporting Device Data to CSV. Do not close or switch the
                        window. Exporting {deviceOffset} / {totalDeviceCount}{' '}
                        devices
                      </DevHubAlert>
                    )}
                    {errorOccurred && (
                      <DevHubAlert variant="outlined" severity="error">
                        Error occurred while exporting. Please try again
                      </DevHubAlert>
                    )}
                  </DevHubBox>
                </DevHubGrid>
                <DevHubGrid item xs={1}>
                  <DevHubBox my={1}>
                    <ExportButton />
                    <CSVLink
                      id="hidden-csv-link"
                      filename={`CloudAssets_${appId}`}
                      data={asExportableDevicesForAudit(
                        sortDevices(dataForExport),
                      )}
                      style={{ display: 'none' }}
                    />
                  </DevHubBox>
                </DevHubGrid>
              </DevHubBox>
            </DevHubGrid>
          )}

          {error ? (
            <DevHubAlert severity="error">Error loading devices</DevHubAlert>
          ) : (
            <DevHubBox>
              <DevHubGrid container spacing={2}>
                {filteredDevices.map((device: Device) => {
                  return (
                    <DevHubGrid
                      key={device.href}
                      item
                      xs={12}
                      lg={6}
                      xl={4}
                      style={{ display: 'flex' }}
                    >
                      <DeviceCard device={device} />
                    </DevHubGrid>
                  );
                })}
              </DevHubGrid>
              {filteredDevices.length === 0 && (
                <DevHubBox mt={2}>
                  <DevHubAlert severity="info">
                    No devices match the current search and filter criteria.
                  </DevHubAlert>
                </DevHubBox>
              )}
            </DevHubBox>
          )}
          <DevHubBox display="flex" justifyContent="center" my={3}>
            <DevHubButton
              disabled={!hasMoreResults || devices.length === 0 || loadingMore}
              variant="contained"
              height="large"
              color="primary"
              onClick={handleLoadMore}
            >
              {getLoadMoreButtonLabel()}
            </DevHubButton>
          </DevHubBox>
        </>
      )}
    </>
  );
}

export default ApplicationDevices;
